import React, { FunctionComponent, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import Grow from '@mui/material/Grow';
import Collapse from '@mui/material/Collapse';

// MUI Icons
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ClearIcon from '@mui/icons-material/Clear';

// Utils
import { useDebouncedCallback } from 'use-debounce';


type PPInfoPaneSearchType = {
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  matches: Array<{ id: string }>;
  goToMatch: (direction: 1 | -1) => void;
  currentMatchIndex: number;
};

const PPInfoPaneSearch: FunctionComponent<PPInfoPaneSearchType> = ({
  searchTerm,
  matches,
  currentMatchIndex,
  goToMatch,
  setSearchTerm,
}) => {

  const { t } = useTranslation();

  /* Memo values */
  const chipLabel = useMemo(() => {
    if (!searchTerm) return null;
    if (!matches.length) return '0/0';
    return `${currentMatchIndex + 1}/${matches.length}`;
  }, [matches, currentMatchIndex, searchTerm]);

  const showActions = useMemo(() => Boolean(searchTerm), [searchTerm]);

  /** Debounced search updates */
  const debouncedSetSearchTerm = useDebouncedCallback(
    (value: string) => setSearchTerm(value),
    200 // debounce delay in ms
  );

  /** Handlers */
  const handleClickPrev = useCallback(() => goToMatch(-1), [goToMatch]);
  const handleClickNext = useCallback(() => goToMatch(1), [goToMatch]);
  const handleClear = useCallback(() => {
    setSearchTerm('');
    const input = document.querySelector('#pp-search-input');
    if (input) (input as HTMLInputElement).value = '';
  }, [debouncedSetSearchTerm]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSetSearchTerm(e.target.value);
    },
    [debouncedSetSearchTerm]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        goToMatch(e.shiftKey ? -1 : 1);
      }
    },
    [goToMatch]
  );

  /* Render */
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5
      }}
    >
      {/* SEARCH INPUT */}
      <Box sx={{ position: 'relative', flexGrow: 1 }}>
        <TextField
          size="small"
          placeholder={t('common.search') as string}
          defaultValue={searchTerm}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          fullWidth
          id='pp-search-input'
          slotProps={{
            input: {
              endAdornment: (
                <Fade in={!!chipLabel} timeout={300}>
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: '30px',
                      textAlign: 'center',
                    }}
                  >
                    {chipLabel}
                  </Typography>
                </Fade>
              ),
            },
          }}
        />
      </Box>

      {/* BUTTONS */}
      <Collapse 
        orientation='horizontal'
        in={showActions} 
        timeout={300}
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            display: 'flex',
            height: '34px',
            alignItems: 'center',
          }}
        >
          <Grow in={showActions} timeout={600}>
            <IconButton 
              disabled={matches.length === 0} 
              size="small" 
              onClick={handleClickPrev}
            >
              <ArrowUpwardIcon />
            </IconButton>
          </Grow>
          <Grow in={showActions} timeout={600} style={{ transitionDelay: showActions ? '100ms' : '0ms' }}>
            <IconButton 
              disabled={matches.length === 0} 
              size="small" 
              onClick={handleClickNext}
            >
              <ArrowDownwardIcon />
            </IconButton>
          </Grow>
          <Grow in={showActions} timeout={600} style={{ transitionDelay: showActions ? '200ms' : '0ms' }}>
            <IconButton 
              size="small" 
              onClick={handleClear}
            >
              <ClearIcon />
            </IconButton>
          </Grow>
        </Box>
      </Collapse>
    </Box>
  );
};

export default PPInfoPaneSearch;